import useMediaQuery from '@mui/material/useMediaQuery'
import { isIosDevice } from '../helpers/platform'

/**
 * Whether this app has to bring a back gesture of its own.
 *
 * The question every custom navigation gesture has to ask first, because the platform's is
 * better than anything that can be built on top: it is driven by the compositor, it shows
 * the actual previous page being uncovered, and it is interruptible.
 *
 * Two places have one already. In a browser tab it is the obvious one. And on iOS an
 * installed web app keeps it too — WebKit's swipe-back is not a piece of browser chrome,
 * so `display: standalone` does not take it away. Ours cannot call it off either: the edge
 * pan is recognised before the page is consulted, and `preventDefault` on a touch that has
 * already started is far too late.
 *
 * Two of them on one finger both run, which is what a swipe out of a conversation looked
 * like: WebKit sliding in its snapshot of the previous entry — the list, still carrying the
 * tab bar it had before the conversation hid it — while our transform pushed the live panes
 * aside *inside* that moving layer. Three slices of the same screen, side by side.
 *
 * So this is true for an installed Android app and nothing else. Android in three-button
 * navigation has no edge gesture at all, and in gesture navigation the system takes the
 * touch outright rather than animating a second copy of the page.
 *
 * Asked as "not in a browser" rather than "is standalone" on purpose: the manifest carries
 * `display_override: ["tabbed"]`, and a browser honouring that reports `tabbed` — which is
 * not `standalone` and has no back gesture either. `browser` is the one value that means
 * "there is chrome around this", so it is the one worth testing.
 */
export const useNeedsBackGesture = (): boolean =>
  !useMediaQuery('(display-mode: browser)') && !isIosDevice()

export default useNeedsBackGesture
